import { Component, TemplateFunction } from '@ribajs/core'
import { SbToastComponent } from '.'

import type { ToastConfig, ToastType } from '../types'

/**
 * A toaster component. This component is used as a container for sb-toast components.
 *
 */
export class SbToasterComponent extends Component {
  public static tagName = 'sb-toaster'

  public scope = {
    // OPTIONS
    // METHODS
    // TEMPLATES
  }

  private indexedToasts: Map<string | symbol, SbToastComponent> = new Map()

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  public removeAllToasts() {
    this.querySelectorAll('sb-toast').forEach(toast => {
      toast.remove()
    })

    this.indexedToasts.clear()
  }

  /**
   * Remove toast added with a specific id
   * @param id  ID of the added toast
   */
  public removeToastById(id: ToastConfig['id']) {
    if (!id) {
      return
    }
    const toast = this.indexedToasts.get(id)
    if (!toast) {
      return
    }
    toast.remove()
    this.indexedToasts.delete(id)
  }

  public addToast(
    message: string,
    type: ToastType,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    const id = config?.id

    if (id) {
      const existingToast = this.indexedToasts.get(id)
      if (existingToast) {
        existingToast.remove()
        this.indexedToasts.delete(id)
      }
    }

    const toastCo = SbToastComponent.create(message, type, scope)
    this.appendChild(toastCo)

    if (id) {
      this.indexedToasts.set(id, toastCo)
    }
  }

  public addSuccess(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.addToast(message, 'success', scope, config)
  }

  public addInfo(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.addToast(message, 'info', scope, config)
  }

  public addWarning(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.addToast(message, 'warning', scope, config)
  }

  public addDanger(
    message: string,
    scope: Record<string, any> = {},
    config?: ToastConfig,
  ) {
    this.addToast(message, 'danger', scope, config)
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbToasterComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbToasterComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    //
  }

  protected removeEventListeners() {
    //
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
